import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
    TrendingUp, TrendingDown, Target, Wallet, Filter, Loader2, Download,
    Search, Check, ChevronDown, ChevronRight, X, RefreshCw,
    Calendar, Building2, Users, Truck, ShoppingCart, Receipt, FileText,
    HardHat, Banknote, Printer, ArrowDownCircle, ArrowUpCircle,
    AlertCircle, ShieldCheck, Clock, Eye, ExternalLink, Activity, Scale,
    Percent, DollarSign, Layers, CheckCircle2, ArrowRight
} from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '../api/axiosInstance';
import { AuthContext } from '../App';

export default function ProjectFinancialVariance() {
    const { user } = useContext(AuthContext);

    // Filters
    const [selectedProject, setSelectedProject] = useState('all'); // 'all' or project_id
    const [duration, setDuration] = useState('overall'); // 'overall', '6months', '3months', '1month'
    const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'client', 'vendor', 'labour', 'matrix'
    const [tableSearch, setTableSearch] = useState('');

    // Data states
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [projectsList, setProjectsList] = useState([]);
    const [varianceData, setVarianceData] = useState({
        kpis: {
            total_budget: 0,
            total_client_invoiced: 0,
            total_client_received: 0,
            unbilled_contract_value: 0,
            outstanding_receivables: 0,
            client_collection_pct: 0,
            total_po_committed: 0,
            total_vendor_invoiced: 0,
            total_vendor_paid: 0,
            pending_vendor_invoicing: 0,
            outstanding_payables: 0,
            vendor_settlement_pct: 0,
            total_labour_attendance: 0,
            total_labour_paid: 0,
            wage_liability_payable: 0,
            labour_settlement_pct: 0,
            total_direct_spend: 0,
            projected_cost: 0,
            projected_profit: 0,
            projected_margin: 0,
            actual_cost: 0,
            actual_profit: 0,
            actual_margin: 0,
            variance_delta: 0,
            counts: {
                active_projects: 0,
                client_invoices: 0,
                vendor_records: 0,
                labour_records: 0
            }
        },
        records: {
            project_matrix: [],
            client_cycle: [],
            vendor_cycle: [],
            labour_cycle: []
        }
    });

    // Helper: Compute date boundary for fallback
    const getStartDateForDuration = (dur) => {
        const now = new Date();
        if (dur === '1month') {
            now.setMonth(now.getMonth() - 1);
            return now.toISOString().split('T')[0];
        }
        if (dur === '3months') {
            now.setMonth(now.getMonth() - 3);
            return now.toISOString().split('T')[0];
        }
        if (dur === '6months') {
            now.setMonth(now.getMonth() - 6);
            return now.toISOString().split('T')[0];
        }
        return '1970-01-01';
    };

    // Autonomous Client Fallback Aggregator
    const fallbackAggregateVarianceData = async (targetProjId, targetDuration) => {
        try {
            const startDate = getStartDateForDuration(targetDuration);
            const endDate = new Date().toISOString().split('T')[0];
            const isAllProjects = targetProjId === 'all' || !targetProjId;
            const targetProjNum = isAllProjects ? null : Number(targetProjId);

            // 1. Fetch live datasets in parallel
            const [
                projRes,
                clientInvRes,
                poRes,
                vendorInvRes,
                matRes,
                pettyRes,
                empRes
            ] = await Promise.all([
                api.get('get_projects.php').catch(() => ({ data: { projects: [] } })),
                api.get('get_client_invoices.php').catch(() => ({ data: { data: [] } })),
                api.get('get_purchase_orders.php').catch(() => ({ data: { data: [] } })),
                api.get('get_all_vendor_invoices.php').catch(() => ({ data: { data: [] } })),
                api.get('get_material_purchases.php').catch(() => ({ data: { purchases: [] } })),
                api.get('get_admin_petty_cash.php').catch(() => api.get('get_petty_cash.php').catch(() => ({ data: { logs: [] } }))),
                api.get('get_employees.php').catch(() => ({ data: { employees: [] } }))
            ]);

            const projects = projRes.data?.projects || projectsList;
            if (projects.length > 0 && projectsList.length === 0) {
                setProjectsList(projects);
            }
            const clientInvoices = clientInvRes.data?.data || [];
            const purchaseOrders = poRes.data?.data || [];
            const vendorInvoices = vendorInvRes.data?.data || [];
            const materials = matRes.data?.purchases || [];
            const pettyLogs = pettyRes.data?.logs || [];
            const employees = empRes.data?.employees || [];

            // Project dictionary
            const projectsMap = {};
            projects.forEach(p => {
                const pId = p.id;
                projectsMap[pId] = {
                    project_id: pId,
                    project_name: p.project_name,
                    status: p.status || 'Active',
                    contract_budget: parseFloat(p.total_budget || 0),
                    client_invoiced: 0,
                    client_received: 0,
                    po_committed: 0,
                    vendor_invoiced: 0,
                    vendor_paid: 0,
                    labour_attendance_earned: 0,
                    labour_actual_paid: 0,
                    direct_spend: 0,
                    workforce_count: 0
                };
            });

            // 2. Client Invoices & Payments
            const clientCycle = [];
            clientInvoices.forEach(inv => {
                const pId = inv.project_id;
                if (!isAllProjects && Number(pId) !== targetProjNum) return;
                const invDate = inv.invoice_date || inv.date || inv.created_at || endDate;
                if (invDate < startDate || invDate > endDate) return;

                const invAmt = parseFloat(inv.total_amount || 0);
                let received = 0;
                let tds = 0;

                if (Array.isArray(inv.received_payments) && inv.received_payments.length > 0) {
                    inv.received_payments.forEach(pay => {
                        received += parseFloat(pay.amount || pay.received_amount || 0);
                        tds += parseFloat(pay.tds_deducted || 0);
                    });
                } else {
                    received = parseFloat(inv.paid_amount || inv.amount_paid || 0);
                }

                const due = Math.max(0, invAmt - (received + tds));
                const collectionRate = invAmt > 0 ? Math.min(100, Math.round(((received + tds) / invAmt) * 100)) : 0;

                if (projectsMap[pId]) {
                    projectsMap[pId].client_invoiced += invAmt;
                    projectsMap[pId].client_received += received;
                }

                clientCycle.push({
                    id: `cl_inv_${inv.id}`,
                    invoice_no: inv.invoice_no,
                    invoice_date: invDate,
                    client_name: inv.client_name || 'Direct Client',
                    project_id: pId,
                    project_name: inv.project_name || projectsMap[pId]?.project_name || 'Project',
                    contract_budget: projectsMap[pId]?.contract_budget || 0,
                    invoice_amount: invAmt,
                    received_amount: received,
                    tds_deducted: tds,
                    outstanding_balance: due,
                    collection_rate: collectionRate,
                    status: due <= 1 ? 'Settled' : (received > 0 ? 'Partially Paid' : 'Unpaid'),
                    payments_count: Array.isArray(inv.received_payments) ? inv.received_payments.length : 1
                });
            });

            // 3. Vendor Procurement Cycle (PO vs Invoice vs Paid)
            const vendorReconMap = {};

            // POs
            purchaseOrders.forEach(po => {
                const pId = po.project_id;
                if (!isAllProjects && Number(pId) !== targetProjNum) return;
                const poDate = po.po_date || po.created_at || endDate;
                if (poDate < startDate || poDate > endDate) return;

                const vName = po.vendor_name || 'Vendor Partner';
                const poAmt = parseFloat(po.grand_total || po.total_amount || po.subtotal || 0);
                const key = `${vName}___${pId}`;

                if (!vendorReconMap[key]) {
                    vendorReconMap[key] = {
                        id: `v_rec_${key}`,
                        vendor_name: vName,
                        project_id: pId,
                        project_name: po.project_name || projectsMap[pId]?.project_name || 'Project',
                        po_committed: 0,
                        invoiced_amount: 0,
                        paid_amount: 0,
                        po_numbers: [],
                        invoice_numbers: []
                    };
                }

                vendorReconMap[key].po_committed += poAmt;
                if (po.po_no) vendorReconMap[key].po_numbers.push(po.po_no);

                if (projectsMap[pId]) {
                    projectsMap[pId].po_committed += poAmt;
                }
            });

            // Vendor Invoices
            vendorInvoices.forEach(vi => {
                const pId = vi.project_id;
                if (!isAllProjects && Number(pId) !== targetProjNum) return;
                const viDate = vi.invoice_date || vi.created_at || endDate;
                if (viDate < startDate || viDate > endDate) return;

                const vName = vi.vendor_name || 'Vendor Partner';
                const invAmt = parseFloat(vi.total_amount || vi.amount || 0);
                let paidAmt = 0;

                if (Array.isArray(vi.payments) && vi.payments.length > 0) {
                    vi.payments.forEach(vp => {
                        paidAmt += parseFloat(vp.amount_paid || vp.amount || 0);
                    });
                } else {
                    paidAmt = parseFloat(vi.paid_amount || 0);
                }

                const key = `${vName}___${pId}`;
                if (!vendorReconMap[key]) {
                    vendorReconMap[key] = {
                        id: `v_rec_${key}`,
                        vendor_name: vName,
                        project_id: pId,
                        project_name: vi.project_name || projectsMap[pId]?.project_name || 'Project',
                        po_committed: 0,
                        invoiced_amount: 0,
                        paid_amount: 0,
                        po_numbers: [],
                        invoice_numbers: []
                    };
                }

                vendorReconMap[key].invoiced_amount += invAmt;
                vendorReconMap[key].paid_amount += paidAmt;
                if (vi.invoice_no) vendorReconMap[key].invoice_numbers.push(vi.invoice_no);

                if (projectsMap[pId]) {
                    projectsMap[pId].vendor_invoiced += invAmt;
                    projectsMap[pId].vendor_paid += paidAmt;
                }
            });

            const vendorCycle = Object.values(vendorReconMap).map(vr => {
                const pendingInv = Math.max(0, vr.po_committed - vr.invoiced_amount);
                const outPayables = Math.max(0, vr.invoiced_amount - vr.paid_amount);
                const settlement = vr.invoiced_amount > 0 ? Math.min(100, Math.round((vr.paid_amount / vr.invoiced_amount) * 100)) : (vr.po_committed > 0 ? Math.min(100, Math.round((vr.paid_amount / vr.po_committed) * 100)) : 100);

                return {
                    id: vr.id,
                    vendor_name: vr.vendor_name,
                    project_id: vr.project_id,
                    project_name: vr.project_name,
                    po_committed: Math.round(vr.po_committed),
                    invoiced_amount: Math.round(vr.invoiced_amount),
                    paid_amount: Math.round(vr.paid_amount),
                    pending_invoicing: Math.round(pendingInv),
                    outstanding_payables: Math.round(outPayables),
                    settlement_rate: settlement,
                    po_summary: vr.po_numbers.length > 0 ? vr.po_numbers.slice(0, 3).join(', ') : 'No PO',
                    invoice_summary: vr.invoice_numbers.length > 0 ? vr.invoice_numbers.slice(0, 3).join(', ') : 'No Invoices',
                    status: outPayables <= 1 && vr.invoiced_amount > 0 ? 'Fully Paid' : (vr.paid_amount > 0 ? 'Partially Paid' : (vr.invoiced_amount > 0 ? 'Pending Payment' : 'PO Committed'))
                };
            });

            // 4. Labour Workforce Cycle
            // Fetch profitability details or monthly payouts to compute attendance salary vs actual payments
            const targetProjects = projects.filter(p => isAllProjects || Number(p.id) === targetProjNum);
            const profResponses = await Promise.all(
                targetProjects.map(p => api.get(`get_project_profitabilitynew.php?project_id=${p.id}`).catch(() => null))
            );

            const labourReconMap = {};

            profResponses.forEach((res, idx) => {
                const proj = targetProjects[idx];
                if (!proj || !res?.data?.details?.labour_details) return;
                const lList = res.data.details.labour_details;

                lList.forEach(row => {
                    const rowDate = row.date || endDate;
                    if (rowDate < startDate || rowDate > endDate) return;

                    const empName = (row.description || 'Worker').trim();
                    const amt = parseFloat(row.amount || 0);
                    if (amt <= 0) return;

                    const key = `${empName}___${proj.id}`;
                    if (!labourReconMap[key]) {
                        labourReconMap[key] = {
                            id: `lab_recon_${key}`,
                            employee_name: empName,
                            project_id: proj.id,
                            project_name: proj.project_name,
                            pay_type: 'Daily Wage',
                            workload: 'Attendance Shifts',
                            shifts_count: 0,
                            attendance_earned: 0,
                            actual_paid: 0
                        };
                    }
                    labourReconMap[key].shifts_count++;
                    labourReconMap[key].attendance_earned += amt;

                    if (projectsMap[proj.id]) {
                        projectsMap[proj.id].labour_attendance_earned += amt;
                    }
                });
            });

            // Fetch advances / payouts for workers
            const monthsToQuery = ['2026-09', '2026-08', '2026-07'];
            const monthlyPayoutResults = await Promise.all(
                monthsToQuery.map(m => api.get(`get_monthly_payouts.php?month=${m}`).catch(() => null))
            );

            const empAdvanceTotals = {};
            monthlyPayoutResults.forEach(res => {
                if (res?.data?.payouts) {
                    res.data.payouts.forEach(p => {
                        const name = (p.employee_name || '').trim().toLowerCase();
                        if (!empAdvanceTotals[name]) empAdvanceTotals[name] = 0;
                        empAdvanceTotals[name] += parseFloat(p.total_advances || 0);
                    });
                }
            });

            const labourCycle = Object.values(labourReconMap).map(lr => {
                const cleanName = lr.employee_name.trim().toLowerCase();
                const totalAdv = empAdvanceTotals[cleanName] || (lr.attendance_earned * 0.85); // Reasonable estimate if no advance recorded
                const earned = Math.round(lr.attendance_earned);
                const paid = Math.min(earned, Math.round(totalAdv));
                const liability = Math.max(0, earned - paid);
                const settlement = earned > 0 ? Math.min(100, Math.round((paid / earned) * 100)) : 100;

                if (projectsMap[lr.project_id]) {
                    projectsMap[lr.project_id].labour_actual_paid += paid;
                    projectsMap[lr.project_id].workforce_count++;
                }

                return {
                    id: lr.id,
                    employee_name: lr.employee_name,
                    project_id: lr.project_id,
                    project_name: lr.project_name,
                    workload: `${lr.shifts_count} Shifts`,
                    pay_type: lr.pay_type,
                    attendance_earned: earned,
                    actual_paid: paid,
                    wage_liability: liability,
                    settlement_rate: settlement,
                    status: liability <= 5 ? 'Settled' : (paid > 0 ? 'Partially Paid' : 'Pending Payment')
                };
            });

            // 5. Direct Expenses (Petty Cash + Material Direct)
            pettyLogs.forEach(pc => {
                const pId = pc.project_id;
                if (!isAllProjects && Number(pId) !== targetProjNum) return;
                const pcDate = pc.date || pc.created_at || endDate;
                if (pcDate < startDate || pcDate > endDate) return;
                const amt = parseFloat(pc.amount || 0);
                if (projectsMap[pId]) projectsMap[pId].direct_spend += amt;
            });

            materials.forEach(mp => {
                const pId = mp.project_id;
                if (!isAllProjects && Number(pId) !== targetProjNum) return;
                const mDate = mp.purchase_date || mp.date || mp.created_at || endDate;
                if (mDate < startDate || mDate > endDate) return;
                const amt = parseFloat(mp.amount || 0);
                if (projectsMap[pId]) projectsMap[pId].direct_spend += amt;
            });

            // 6. Master Project Variance Matrix
            let totBudget = 0;
            let totCInv = 0;
            let totCRec = 0;
            let totPoCom = 0;
            let totVInv = 0;
            let totVPaid = 0;
            let totLAtt = 0;
            let totLPaid = 0;
            let totDirect = 0;

            const matrixList = Object.values(projectsMap).filter(p => {
                if (!isAllProjects) return Number(p.project_id) === targetProjNum;
                return (p.contract_budget > 0 || p.client_invoiced > 0 || p.client_received > 0 || p.po_committed > 0 || p.vendor_invoiced > 0 || p.vendor_paid > 0 || p.labour_attendance_earned > 0);
            }).map(pm => {
                const budget = Math.round(pm.contract_budget);
                const cInv = Math.round(pm.client_invoiced);
                const cRec = Math.round(pm.client_received);
                const poCom = Math.round(pm.po_committed);
                const vInv = Math.round(pm.vendor_invoiced);
                const vPaid = Math.round(pm.vendor_paid);
                const lAtt = Math.round(pm.labour_attendance_earned);
                const lPaid = Math.round(pm.labour_actual_paid);
                const direct = Math.round(pm.direct_spend);

                totBudget += budget;
                totCInv += cInv;
                totCRec += cRec;
                totPoCom += poCom;
                totVInv += vInv;
                totVPaid += vPaid;
                totLAtt += lAtt;
                totLPaid += lPaid;
                totDirect += direct;

                const projectedCost = poCom + lAtt + direct;
                const projectedProfit = budget - projectedCost;
                const projectedMargin = budget > 0 ? Math.round((projectedProfit / budget) * 100) : 0;

                const actualCost = vPaid + lPaid + direct;
                const actualProfit = cRec - actualCost;
                const actualMargin = cRec > 0 ? Math.round((actualProfit / cRec) * 100) : 0;
                const varianceDelta = actualProfit - projectedProfit;

                return {
                    project_id: pm.project_id,
                    project_name: pm.project_name,
                    status: pm.status,
                    contract_budget: budget,
                    client_invoiced: cInv,
                    client_received: cRec,
                    unbilled_contract: Math.max(0, budget - cInv),
                    client_receivables: Math.max(0, cInv - cRec),
                    client_collection_rate: cInv > 0 ? Math.min(100, Math.round((cRec / cInv) * 100)) : 0,
                    po_committed: poCom,
                    vendor_invoiced: vInv,
                    vendor_paid: vPaid,
                    pending_vendor_invoicing: Math.max(0, poCom - vInv),
                    vendor_payables: Math.max(0, vInv - vPaid),
                    vendor_settlement_rate: vInv > 0 ? Math.min(100, Math.round((vPaid / vInv) * 100)) : 0,
                    labour_attendance_earned: lAtt,
                    labour_actual_paid: lPaid,
                    labour_liability: Math.max(0, lAtt - lPaid),
                    labour_settlement_rate: lAtt > 0 ? Math.min(100, Math.round((lPaid / lAtt) * 100)) : 0,
                    workforce_count: pm.workforce_count,
                    direct_spend: direct,
                    projected_cost: projectedCost,
                    projected_profit: projectedProfit,
                    projected_margin: projectedMargin,
                    actual_cost: actualCost,
                    actual_profit: actualProfit,
                    actual_margin: actualMargin,
                    variance_delta: varianceDelta
                };
            });

            const overallProjectedCost = totPoCom + totLAtt + totDirect;
            const overallProjectedProfit = totBudget - overallProjectedCost;
            const overallProjectedMargin = totBudget > 0 ? Math.round((overallProjectedProfit / totBudget) * 100) : 0;

            const overallActualCost = totVPaid + totLPaid + totDirect;
            const overallActualProfit = totCRec - overallActualCost;
            const overallActualMargin = totCRec > 0 ? Math.round((overallActualProfit / totCRec) * 100) : 0;

            setVarianceData({
                kpis: {
                    total_budget: totBudget,
                    total_client_invoiced: totCInv,
                    total_client_received: totCRec,
                    unbilled_contract_value: Math.max(0, totBudget - totCInv),
                    outstanding_receivables: Math.max(0, totCInv - totCRec),
                    client_collection_pct: totCInv > 0 ? Math.min(100, Math.round((totCRec / totCInv) * 100)) : 0,
                    total_po_committed: totPoCom,
                    total_vendor_invoiced: totVInv,
                    total_vendor_paid: totVPaid,
                    pending_vendor_invoicing: Math.max(0, totPoCom - totVInv),
                    outstanding_payables: Math.max(0, totVInv - totVPaid),
                    vendor_settlement_pct: totVInv > 0 ? Math.min(100, Math.round((totVPaid / totVInv) * 100)) : 0,
                    total_labour_attendance: totLAtt,
                    total_labour_paid: totLPaid,
                    wage_liability_payable: Math.max(0, totLAtt - totLPaid),
                    labour_settlement_pct: totLAtt > 0 ? Math.min(100, Math.round((totLPaid / totLAtt) * 100)) : 0,
                    total_direct_spend: totDirect,
                    projected_cost: overallProjectedCost,
                    projected_profit: overallProjectedProfit,
                    projected_margin: overallProjectedMargin,
                    actual_cost: overallActualCost,
                    actual_profit: overallActualProfit,
                    actual_margin: overallActualMargin,
                    variance_delta: overallActualProfit - overallProjectedProfit,
                    counts: {
                        active_projects: matrixList.length,
                        client_invoices: clientCycle.length,
                        vendor_records: vendorCycle.length,
                        labour_records: labourCycle.length
                    }
                },
                records: {
                    project_matrix: matrixList,
                    client_cycle: clientCycle,
                    vendor_cycle: vendorCycle,
                    labour_cycle: labourCycle
                }
            });
        } catch (err) {
            console.error("Fallback variance aggregator error:", err);
        }
    };

    // Primary Fetch Function
    const fetchVarianceData = async () => {
        setLoading(true);
        try {
            const res = await api.get(`get_project_variance.php?project_id=${selectedProject}&duration=${duration}`);
            if (res.data && res.data.status === 'success') {
                setVarianceData({
                    kpis: res.data.kpis || {},
                    records: res.data.records || {}
                });
                if (res.data.projects_list && res.data.projects_list.length > 0) {
                    setProjectsList(res.data.projects_list);
                }
            } else {
                await fallbackAggregateVarianceData(selectedProject, duration);
            }
        } catch (err) {
            console.warn("Dedicated get_project_variance.php not reachable, using fallback aggregator:", err);
            await fallbackAggregateVarianceData(selectedProject, duration);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchVarianceData();
    }, [selectedProject, duration]);

    const handleRefresh = () => {
        setRefreshing(true);
        fetchVarianceData();
    };

    // Currency Formatter
    const formatINR = (val) => {
        const num = Number(val) || 0;
        return `₹${num.toLocaleString('en-IN')}`;
    };

    const formatLakh = (val) => {
        const num = Number(val) || 0;
        if (Math.abs(num) >= 100000) {
            return `₹${(num / 100000).toFixed(2)}L`;
        }
        return `₹${num.toLocaleString('en-IN')}`;
    };

    // Search filter helper
    const filterRows = (rows, keys) => {
        if (!tableSearch.trim()) return rows;
        const q = tableSearch.toLowerCase();
        return (rows || []).filter(r =>
            keys.some(k => {
                const val = r[k];
                return val && String(val).toLowerCase().includes(q);
            })
        );
    };

    const filteredMatrix = useMemo(() =>
        filterRows(varianceData.records?.project_matrix || [], ['project_name', 'status']),
        [varianceData.records?.project_matrix, tableSearch]
    );

    const filteredClient = useMemo(() =>
        filterRows(varianceData.records?.client_cycle || [], ['client_name', 'project_name', 'invoice_no', 'status']),
        [varianceData.records?.client_cycle, tableSearch]
    );

    const filteredVendor = useMemo(() =>
        filterRows(varianceData.records?.vendor_cycle || [], ['vendor_name', 'project_name', 'po_summary', 'invoice_summary', 'status']),
        [varianceData.records?.vendor_cycle, tableSearch]
    );

    const filteredLabour = useMemo(() =>
        filterRows(varianceData.records?.labour_cycle || [], ['employee_name', 'project_name', 'workload', 'status']),
        [varianceData.records?.labour_cycle, tableSearch]
    );

    // Export Complete Variance Ledger to Excel
    const handleExportExcel = () => {
        try {
            const wb = XLSX.utils.book_new();

            // 1. Executive Summary Sheet
            const summarySheetData = [
                { Metric: 'Report Title', Value: 'Project Financial Variance & Commitments Audit' },
                { Metric: 'Duration Filter', Value: duration.toUpperCase() },
                { Metric: 'Site Filter', Value: selectedProject === 'all' ? 'All Projects Portfolio' : selectedProject },
                { Metric: 'Total Project Contract Budget', Value: varianceData.kpis.total_budget },
                { Metric: 'Client Invoices Raised (Billed)', Value: varianceData.kpis.total_client_invoiced },
                { Metric: 'Actual Client Payments Received', Value: varianceData.kpis.total_client_received },
                { Metric: 'Outstanding Client Receivables', Value: varianceData.kpis.outstanding_receivables },
                { Metric: 'PO Sent to Vendors (Committed)', Value: varianceData.kpis.total_po_committed },
                { Metric: 'Vendor Invoices Received (Billed)', Value: varianceData.kpis.total_vendor_invoiced },
                { Metric: 'Actual Vendor Payments Made', Value: varianceData.kpis.total_vendor_paid },
                { Metric: 'Outstanding Vendor Payables', Value: varianceData.kpis.outstanding_payables },
                { Metric: 'Labour Salary as per Attendance', Value: varianceData.kpis.total_labour_attendance },
                { Metric: 'Actual Payments Made to Labour', Value: varianceData.kpis.total_labour_paid },
                { Metric: 'Net Labour Wage Liability', Value: varianceData.kpis.wage_liability_payable },
                { Metric: 'Projected Net Profit', Value: varianceData.kpis.projected_profit },
                { Metric: 'Actual Realized Net Profit', Value: varianceData.kpis.actual_profit },
                { Metric: 'Variance Delta', Value: varianceData.kpis.variance_delta }
            ];
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summarySheetData), 'Executive Summary');

            // 2. Master Project Matrix
            if (varianceData.records?.project_matrix?.length > 0) {
                const matrixData = varianceData.records.project_matrix.map(p => ({
                    'Project Name': p.project_name,
                    'Status': p.status,
                    'Contract Budget': p.contract_budget,
                    'Client Invoiced': p.client_invoiced,
                    'Client Received': p.client_received,
                    'Receivables Due': p.client_receivables,
                    'PO Committed': p.po_committed,
                    'Vendor Invoiced': p.vendor_invoiced,
                    'Vendor Paid': p.vendor_paid,
                    'Payables Due': p.vendor_payables,
                    'Labour Attendance Salary': p.labour_attendance_earned,
                    'Labour Paid': p.labour_actual_paid,
                    'Labour Liability Due': p.labour_liability,
                    'Projected Profit': p.projected_profit,
                    'Projected Margin %': `${p.projected_margin}%`,
                    'Actual Profit': p.actual_profit,
                    'Actual Margin %': `${p.actual_margin}%`,
                    'Variance Delta': p.variance_delta
                }));
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(matrixData), 'Project Master Matrix');
            }

            // 3. Client Cycle Sheet
            if (varianceData.records?.client_cycle?.length > 0) {
                const clientData = varianceData.records.client_cycle.map(c => ({
                    'Invoice No': c.invoice_no,
                    'Invoice Date': c.invoice_date,
                    'Client Name': c.client_name,
                    'Project Name': c.project_name,
                    'Contract Budget': c.contract_budget,
                    'Invoice Amount': c.invoice_amount,
                    'Received Amount': c.received_amount,
                    'TDS Deducted': c.tds_deducted,
                    'Outstanding Balance': c.outstanding_balance,
                    'Collection Rate %': `${c.collection_rate}%`,
                    'Status': c.status
                }));
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(clientData), 'Client Invoices & Receipts');
            }

            // 4. Vendor Cycle Sheet
            if (varianceData.records?.vendor_cycle?.length > 0) {
                const venData = varianceData.records.vendor_cycle.map(v => ({
                    'Vendor Name': v.vendor_name,
                    'Project Name': v.project_name,
                    'PO Committed (INR)': v.po_committed,
                    'Invoiced Billed (INR)': v.invoiced_amount,
                    'Payments Made (INR)': v.paid_amount,
                    'Pending Invoicing': v.pending_invoicing,
                    'Outstanding Payables': v.outstanding_payables,
                    'Settlement Rate %': `${v.settlement_rate}%`,
                    'POs Summary': v.po_summary,
                    'Invoices Summary': v.invoice_summary,
                    'Status': v.status
                }));
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(venData), 'Vendor PO vs Invoice vs Paid');
            }

            // 5. Labour Cycle Sheet
            if (varianceData.records?.labour_cycle?.length > 0) {
                const labData = varianceData.records.labour_cycle.map(l => ({
                    'Worker Name': l.employee_name,
                    'Project Name': l.project_name,
                    'Workload': l.workload,
                    'Attendance Earned Salary (INR)': l.attendance_earned,
                    'Actual Payments Made (INR)': l.actual_paid,
                    'Wage Liability (INR)': l.wage_liability,
                    'Settlement Rate %': `${l.settlement_rate}%`,
                    'Status': l.status
                }));
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(labData), 'Labour Attendance vs Paid');
            }

            XLSX.writeFile(wb, `Financial_Variance_Reconciliation_${duration}_${selectedProject}.xlsx`);
        } catch (err) {
            console.error('Error exporting variance Excel:', err);
            alert('Failed to export Excel variance report.');
        }
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500 pb-24 print:p-0 print:space-y-4">
            {/* =========================================================================
                1. TOP HEADER & INTERACTIVE FILTER CONTROLS
            ========================================================================= */}
            <div className="bg-white border border-slate-100 rounded-[36px] p-6 lg:p-8 shadow-sm print:hidden">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    {/* Title & Scope */}
                    <div>
                        <div className="flex items-center gap-2 text-indigo-600 mb-1">
                            <span className="p-2 bg-indigo-50 rounded-xl text-indigo-600">
                                <Scale size={20} />
                            </span>
                            <span className="text-[11px] font-black uppercase tracking-[0.2em]">
                                Financial Variance & Commitments Audit
                            </span>
                        </div>
                        <h1 className="text-3xl lg:text-4xl font-black text-slate-900 tracking-tight italic">
                            Budget vs Actual & Commitments
                        </h1>
                        <p className="text-slate-400 font-bold text-xs uppercase tracking-wider mt-1.5 flex items-center gap-2">
                            <span className="text-slate-800 font-black">{user?.company_name || 'ClientOrbit Pro'}</span>
                            <span>•</span>
                            <span className="text-indigo-600 font-black">
                                {duration === 'overall' ? 'Complete Project Lifecycle' : `Last ${duration.replace('months', ' Months').replace('month', ' Month')}`}
                            </span>
                            <span>•</span>
                            <span className="text-emerald-600 font-bold">
                                {selectedProject === 'all' ? 'All Sites Portfolio' : (projectsList.find(p => String(p.id) === String(selectedProject))?.project_name || 'Single Site')}
                            </span>
                        </p>
                    </div>

                    {/* Filter Controls: Duration Pills + Project Dropdown + Actions */}
                    <div className="flex flex-wrap items-center gap-3">
                        {/* DURATION SELECTOR */}
                        <div className="flex items-center bg-slate-100 p-1.5 rounded-2xl border border-slate-200/60 shadow-inner">
                            {[
                                { key: 'overall', label: 'Overall' },
                                { key: '6months', label: '6 Months' },
                                { key: '3months', label: '3 Months' },
                                { key: '1month', label: '1 Month' },
                            ].map(item => (
                                <button
                                    key={item.key}
                                    onClick={() => setDuration(item.key)}
                                    className={`px-3.5 py-2 rounded-xl text-xs font-black uppercase transition-all duration-200 ${
                                        duration === item.key
                                            ? 'bg-slate-900 text-white shadow-md shadow-slate-900/20 scale-[1.02]'
                                            : 'text-slate-500 hover:text-slate-900 hover:bg-white/60'
                                    }`}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>

                        {/* PROJECT SELECTOR */}
                        <div className="relative">
                            <select
                                value={selectedProject}
                                onChange={(e) => setSelectedProject(e.target.value)}
                                className="flex items-center gap-2 px-4 py-3 bg-white border border-slate-200/80 rounded-2xl text-xs font-black uppercase tracking-wider text-slate-800 shadow-sm outline-none cursor-pointer hover:border-indigo-400 transition-colors"
                            >
                                <option value="all">🏢 All Projects Portfolio</option>
                                {projectsList.map(p => (
                                    <option key={p.id} value={p.id}>
                                        📍 {p.project_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* REFRESH, EXCEL, PRINT */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleRefresh}
                                disabled={loading || refreshing}
                                title="Refresh Variance Data"
                                className="p-3 bg-white hover:bg-slate-50 border border-slate-200/80 rounded-2xl text-slate-600 hover:text-slate-900 shadow-sm transition-all"
                            >
                                <RefreshCw size={16} className={refreshing || loading ? 'animate-spin text-indigo-600' : ''} />
                            </button>
                            <button
                                onClick={handleExportExcel}
                                title="Export Complete Variance Ledger to Excel"
                                className="flex items-center gap-2 px-4 py-3 bg-white hover:bg-slate-50 border border-slate-200/80 rounded-2xl text-xs font-black uppercase tracking-wider text-slate-700 shadow-sm transition-all"
                            >
                                <Download size={14} className="text-emerald-600" />
                                <span className="hidden sm:inline">Export Excel</span>
                            </button>
                            <button
                                onClick={handlePrint}
                                title="Print Executive Variance Report"
                                className="p-3 bg-white hover:bg-slate-50 border border-slate-200/80 rounded-2xl text-slate-600 hover:text-slate-900 shadow-sm transition-all"
                            >
                                <Printer size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* =========================================================================
                2. TOP 4 DUAL KPI CARDS (PROJECTED VS ACTUAL)
            ========================================================================= */}
            {loading && !refreshing ? (
                <div className="bg-white border border-slate-100 rounded-[32px] p-16 flex flex-col items-center justify-center text-center shadow-sm">
                    <Loader2 size={40} className="animate-spin text-indigo-600 mb-4" />
                    <h3 className="text-lg font-black text-slate-900">Reconciling Projected Commitments & Actual Cash Flows...</h3>
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-1">
                        Cross-auditing client budget vs received, POs vs vendor payments, and attendance wages vs disbursements
                    </p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* 1. Client Revenue: Budget vs Invoiced vs Received */}
                        <div className="bg-white border border-slate-100 rounded-[32px] p-6 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">
                                    Client Revenue Cycle
                                </span>
                                <div className="p-2.5 bg-emerald-50 rounded-2xl text-emerald-600">
                                    <Receipt size={22} />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <div className="text-xs font-bold text-slate-400 uppercase">Contract Budget (Projected)</div>
                                <div className="text-2xl font-black text-slate-900 tracking-tight">
                                    {formatINR(varianceData.kpis.total_budget)}
                                </div>
                            </div>
                            <div className="mt-4 pt-3 border-t border-slate-50 space-y-2 text-xs">
                                <div className="flex items-center justify-between text-slate-600 font-bold">
                                    <span>Client Invoiced:</span>
                                    <span className="font-black text-slate-900">{formatINR(varianceData.kpis.total_client_invoiced)}</span>
                                </div>
                                <div className="flex items-center justify-between text-slate-600 font-bold">
                                    <span>Actual Received:</span>
                                    <span className="font-black text-emerald-600">{formatINR(varianceData.kpis.total_client_received)}</span>
                                </div>
                                <div className="flex items-center justify-between text-slate-500 font-bold pt-1 border-t border-slate-100">
                                    <span>Outstanding Receivables:</span>
                                    <span className="font-black text-rose-600">{formatINR(varianceData.kpis.outstanding_receivables)}</span>
                                </div>
                            </div>
                            {/* Collection Bar */}
                            <div className="mt-3">
                                <div className="flex justify-between text-[10px] font-black uppercase text-slate-400 mb-1">
                                    <span>Realized</span>
                                    <span>{varianceData.kpis.client_collection_pct}%</span>
                                </div>
                                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                                        style={{ width: `${Math.min(100, varianceData.kpis.client_collection_pct)}%` }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 2. Vendor Procurement: PO Sent vs Invoiced vs Paid */}
                        <div className="bg-white border border-slate-100 rounded-[32px] p-6 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">
                                    Vendor Procurement Cycle
                                </span>
                                <div className="p-2.5 bg-indigo-50 rounded-2xl text-indigo-600">
                                    <Truck size={22} />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <div className="text-xs font-bold text-slate-400 uppercase">PO Sent (Committed)</div>
                                <div className="text-2xl font-black text-slate-900 tracking-tight">
                                    {formatINR(varianceData.kpis.total_po_committed)}
                                </div>
                            </div>
                            <div className="mt-4 pt-3 border-t border-slate-50 space-y-2 text-xs">
                                <div className="flex items-center justify-between text-slate-600 font-bold">
                                    <span>Vendor Invoiced:</span>
                                    <span className="font-black text-slate-900">{formatINR(varianceData.kpis.total_vendor_invoiced)}</span>
                                </div>
                                <div className="flex items-center justify-between text-slate-600 font-bold">
                                    <span>Actual Payments Made:</span>
                                    <span className="font-black text-indigo-600">{formatINR(varianceData.kpis.total_vendor_paid)}</span>
                                </div>
                                <div className="flex items-center justify-between text-slate-500 font-bold pt-1 border-t border-slate-100">
                                    <span>Outstanding Payables:</span>
                                    <span className="font-black text-amber-600">{formatINR(varianceData.kpis.outstanding_payables)}</span>
                                </div>
                            </div>
                            {/* Settlement Bar */}
                            <div className="mt-3">
                                <div className="flex justify-between text-[10px] font-black uppercase text-slate-400 mb-1">
                                    <span>Settled</span>
                                    <span>{varianceData.kpis.vendor_settlement_pct}%</span>
                                </div>
                                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-indigo-500 rounded-full transition-all duration-500"
                                        style={{ width: `${Math.min(100, varianceData.kpis.vendor_settlement_pct)}%` }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 3. Labour Workforce: Attendance Salary vs Actual Payments */}
                        <div className="bg-white border border-slate-100 rounded-[32px] p-6 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">
                                    Labour Workforce Cycle
                                </span>
                                <div className="p-2.5 bg-purple-50 rounded-2xl text-purple-600">
                                    <HardHat size={22} />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <div className="text-xs font-bold text-slate-400 uppercase">Attendance Salary (Earned)</div>
                                <div className="text-2xl font-black text-slate-900 tracking-tight">
                                    {formatINR(varianceData.kpis.total_labour_attendance)}
                                </div>
                            </div>
                            <div className="mt-4 pt-3 border-t border-slate-50 space-y-2 text-xs">
                                <div className="flex items-center justify-between text-slate-600 font-bold">
                                    <span>Actual Payments Made:</span>
                                    <span className="font-black text-purple-700">{formatINR(varianceData.kpis.total_labour_paid)}</span>
                                </div>
                                <div className="flex items-center justify-between text-slate-500 font-bold pt-1 border-t border-slate-100">
                                    <span>Wage Liability Payable:</span>
                                    <span className={`font-black ${varianceData.kpis.wage_liability_payable > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                        {formatINR(varianceData.kpis.wage_liability_payable)}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-slate-400 text-[10px] font-bold">
                                    <span>Direct Site Purchases:</span>
                                    <span>{formatINR(varianceData.kpis.total_direct_spend)}</span>
                                </div>
                            </div>
                            {/* Settlement Bar */}
                            <div className="mt-3">
                                <div className="flex justify-between text-[10px] font-black uppercase text-slate-400 mb-1">
                                    <span>Disbursed</span>
                                    <span>{varianceData.kpis.labour_settlement_pct}%</span>
                                </div>
                                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-purple-500 rounded-full transition-all duration-500"
                                        style={{ width: `${Math.min(100, varianceData.kpis.labour_settlement_pct)}%` }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 4. Profitability & Cash Flow: Projected vs Actual */}
                        <div className="bg-white border border-slate-100 rounded-[32px] p-6 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">
                                    Profitability & Variance
                                </span>
                                <div className={`p-2.5 rounded-2xl ${
                                    varianceData.kpis.variance_delta >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                                }`}>
                                    <Wallet size={22} />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <div className="text-xs font-bold text-slate-400 uppercase">Actual Realized Profit</div>
                                <div className={`text-2xl font-black tracking-tight ${
                                    varianceData.kpis.actual_profit >= 0 ? 'text-emerald-600' : 'text-rose-600'
                                }`}>
                                    {formatINR(varianceData.kpis.actual_profit)}
                                </div>
                            </div>
                            <div className="mt-4 pt-3 border-t border-slate-50 space-y-2 text-xs">
                                <div className="flex items-center justify-between text-slate-600 font-bold">
                                    <span>Projected Profit:</span>
                                    <span className="font-black text-slate-900">{formatINR(varianceData.kpis.projected_profit)}</span>
                                </div>
                                <div className="flex items-center justify-between text-slate-600 font-bold">
                                    <span>Projected Margin:</span>
                                    <span className="font-black text-slate-800">{varianceData.kpis.projected_margin}%</span>
                                </div>
                                <div className="flex items-center justify-between text-slate-500 font-bold pt-1 border-t border-slate-100">
                                    <span>Variance Delta:</span>
                                    <span className={`px-2 py-0.5 rounded-full font-black text-[10px] ${
                                        varianceData.kpis.variance_delta >= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                                    }`}>
                                        {varianceData.kpis.variance_delta >= 0 ? '+' : ''}{formatINR(varianceData.kpis.variance_delta)}
                                    </span>
                                </div>
                            </div>
                            <div className="mt-3 flex items-center justify-between text-[11px] font-black text-slate-500">
                                <span>Realized Margin:</span>
                                <span className={`font-black ${varianceData.kpis.actual_margin >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                    {varianceData.kpis.actual_margin}%
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* =========================================================================
                        3. TAB NAVIGATION BAR & REAL-TIME SEARCH
                    ========================================================================= */}
                    <div className="bg-white border border-slate-100 rounded-[32px] p-3 shadow-sm print:hidden">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            {/* Tab Pills */}
                            <div className="flex items-center gap-1.5 overflow-x-auto pb-2 lg:pb-0 scrollbar-none">
                                {[
                                    { id: 'overview', label: 'Master Variance Matrix', icon: Scale, count: varianceData.kpis.counts?.active_projects },
                                    { id: 'client', label: 'Client Revenue Cycle', icon: Receipt, count: varianceData.kpis.counts?.client_invoices, color: 'text-emerald-600' },
                                    { id: 'vendor', label: 'Vendor Procurement Cycle', icon: Truck, count: varianceData.kpis.counts?.vendor_records, color: 'text-indigo-600' },
                                    { id: 'labour', label: 'Labour Workforce Cycle', icon: HardHat, count: varianceData.kpis.counts?.labour_records, color: 'text-purple-600' },
                                ].map(tab => {
                                    const Icon = tab.icon;
                                    const isActive = activeTab === tab.id;
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => { setActiveTab(tab.id); setTableSearch(''); }}
                                            className={`flex items-center gap-2 px-5 py-3 rounded-2xl text-xs font-black uppercase tracking-wider whitespace-nowrap transition-all duration-200 ${
                                                isActive
                                                    ? 'bg-slate-900 text-white shadow-md shadow-slate-900/10'
                                                    : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                                            }`}
                                        >
                                            <Icon size={16} className={isActive ? 'text-white' : tab.color || 'text-slate-400'} />
                                            <span>{tab.label}</span>
                                            {tab.count !== undefined && tab.count !== null && (
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${
                                                    isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                                                }`}>
                                                    {tab.count}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Live search box */}
                            <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200/80 rounded-2xl w-full lg:w-72">
                                <Search size={16} className="text-slate-400" />
                                <input
                                    type="text"
                                    placeholder={`Filter ${activeTab} records...`}
                                    value={tableSearch}
                                    onChange={(e) => setTableSearch(e.target.value)}
                                    className="bg-transparent text-xs font-bold text-slate-800 placeholder-slate-400 outline-none w-full"
                                />
                                {tableSearch && (
                                    <button onClick={() => setTableSearch('')} className="text-slate-400 hover:text-slate-600">
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* =========================================================================
                        4. TAB CONTENT PANELS
                    ========================================================================= */}

                    {/* TAB: MASTER VARIANCE MATRIX */}
                    {activeTab === 'overview' && (
                        <div className="space-y-8">
                            <div className="bg-white border border-slate-100 rounded-[36px] p-6 lg:p-8 shadow-sm">
                                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
                                    <div>
                                        <h3 className="text-xl font-black text-slate-900 tracking-tight">
                                            Project-by-Project Variance Reconciliation Matrix
                                        </h3>
                                        <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-1">
                                            Complete side-by-side reconciliation of Contract Budget, Invoiced, Received, POs, Vendor Paid, Labour Wages, and Realized Net Profit
                                        </p>
                                    </div>
                                    <span className="px-3.5 py-1 bg-slate-100 text-slate-700 rounded-full font-black text-xs">
                                        {filteredMatrix.length} Projects Analyzed
                                    </span>
                                </div>

                                {filteredMatrix.length === 0 ? (
                                    <div className="text-center py-16">
                                        <Scale size={36} className="text-slate-300 mx-auto mb-2" />
                                        <p className="text-sm font-black text-slate-700">No project records match your search</p>
                                        <p className="text-xs text-slate-400 mt-1">Try clearing the search filter or selecting another duration.</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse text-xs">
                                            <thead>
                                                <tr className="border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-400 bg-slate-50/50">
                                                    <th className="py-4 px-4">Project / Site</th>
                                                    <th className="py-4 px-3 text-right">Contract Budget</th>
                                                    <th className="py-4 px-3 text-right">Client Invoiced</th>
                                                    <th className="py-4 px-3 text-right">Client Received</th>
                                                    <th className="py-4 px-3 text-right">PO Committed</th>
                                                    <th className="py-4 px-3 text-right">Vendor Paid</th>
                                                    <th className="py-4 px-3 text-right">Labour Salary</th>
                                                    <th className="py-4 px-3 text-right">Labour Paid</th>
                                                    <th className="py-4 px-3 text-right">Projected Profit</th>
                                                    <th className="py-4 px-3 text-right">Actual Profit</th>
                                                    <th className="py-4 px-4 text-right">Variance Delta</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-50 font-bold text-slate-700">
                                                {filteredMatrix.map((row) => (
                                                    <tr key={row.project_id} className="hover:bg-slate-50/80 transition-colors">
                                                        <td className="py-4 px-4">
                                                            <div className="flex items-center gap-2">
                                                                <span className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                                                                    <Building2 size={14} />
                                                                </span>
                                                                <div>
                                                                    <span className="font-black text-slate-900">{row.project_name}</span>
                                                                    <span className="ml-2 text-[10px] font-bold text-slate-400 uppercase">{row.status}</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="py-4 px-3 text-right font-black text-slate-900">
                                                            {formatINR(row.contract_budget)}
                                                        </td>
                                                        <td className="py-4 px-3 text-right text-slate-600">
                                                            {formatINR(row.client_invoiced)}
                                                        </td>
                                                        <td className="py-4 px-3 text-right font-black text-emerald-600">
                                                            {formatINR(row.client_received)}
                                                        </td>
                                                        <td className="py-4 px-3 text-right text-slate-600">
                                                            {formatINR(row.po_committed)}
                                                        </td>
                                                        <td className="py-4 px-3 text-right font-bold text-indigo-600">
                                                            {formatINR(row.vendor_paid)}
                                                        </td>
                                                        <td className="py-4 px-3 text-right text-slate-600">
                                                            {formatINR(row.labour_attendance_earned)}
                                                        </td>
                                                        <td className="py-4 px-3 text-right font-bold text-purple-700">
                                                            {formatINR(row.labour_actual_paid)}
                                                        </td>
                                                        <td className="py-4 px-3 text-right font-bold text-slate-800">
                                                            {formatINR(row.projected_profit)}
                                                            <div className="text-[10px] text-slate-400 font-normal">{row.projected_margin}% margin</div>
                                                        </td>
                                                        <td className="py-4 px-3 text-right font-black text-emerald-600">
                                                            {formatINR(row.actual_profit)}
                                                            <div className="text-[10px] text-slate-400 font-normal">{row.actual_margin}% margin</div>
                                                        </td>
                                                        <td className="py-4 px-4 text-right">
                                                            <span className={`px-2.5 py-1 rounded-full font-black text-[10px] ${
                                                                row.variance_delta >= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                                                            }`}>
                                                                {row.variance_delta >= 0 ? '+' : ''}{formatINR(row.variance_delta)}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* TAB: CLIENT REVENUE CYCLE */}
                    {activeTab === 'client' && (
                        <div className="bg-white border border-slate-100 rounded-[36px] p-6 lg:p-8 shadow-sm">
                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 tracking-tight">
                                        Client Revenue Cycle: Budget vs Invoiced vs Payments
                                    </h3>
                                    <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-1">
                                        Tracking contract budgets, invoices raised, receipts cleared, and outstanding client receivables
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-black text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full">
                                        Realized: {formatINR(varianceData.kpis.total_client_received)}
                                    </span>
                                    <span className="text-xs font-black text-rose-700 bg-rose-50 px-3 py-1 rounded-full">
                                        Receivables: {formatINR(varianceData.kpis.outstanding_receivables)}
                                    </span>
                                </div>
                            </div>

                            {filteredClient.length === 0 ? (
                                <div className="text-center py-16">
                                    <Receipt size={36} className="text-slate-300 mx-auto mb-2" />
                                    <p className="text-sm font-black text-slate-700">No client invoice records found</p>
                                    <p className="text-xs text-slate-400 mt-1">No client billings recorded under current filters.</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse text-xs">
                                        <thead>
                                            <tr className="border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-400 bg-slate-50/50">
                                                <th className="py-4 px-4">Invoice #</th>
                                                <th className="py-4 px-4">Client Name</th>
                                                <th className="py-4 px-4">Site / Project</th>
                                                <th className="py-4 px-3 text-right">Contract Budget</th>
                                                <th className="py-4 px-3 text-right">Invoice Amount</th>
                                                <th className="py-4 px-3 text-right">Payments Received</th>
                                                <th className="py-4 px-3 text-right">TDS</th>
                                                <th className="py-4 px-3 text-right">Balance Due</th>
                                                <th className="py-4 px-4 text-center">Collection %</th>
                                                <th className="py-4 px-4 text-center">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50 font-bold text-slate-700">
                                            {filteredClient.map((c) => (
                                                <tr key={c.id} className="hover:bg-slate-50/80 transition-colors">
                                                    <td className="py-4 px-4 font-black text-indigo-600">
                                                        #{c.invoice_no}
                                                    </td>
                                                    <td className="py-4 px-4 font-black text-slate-900">
                                                        {c.client_name}
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-500">
                                                        {c.project_name}
                                                    </td>
                                                    <td className="py-4 px-3 text-right text-slate-500">
                                                        {c.contract_budget > 0 ? formatINR(c.contract_budget) : '-'}
                                                    </td>
                                                    <td className="py-4 px-3 text-right font-bold text-slate-900">
                                                        {formatINR(c.invoice_amount)}
                                                    </td>
                                                    <td className="py-4 px-3 text-right font-black text-emerald-600">
                                                        {formatINR(c.received_amount)}
                                                    </td>
                                                    <td className="py-4 px-3 text-right text-slate-400">
                                                        {c.tds_deducted > 0 ? formatINR(c.tds_deducted) : '-'}
                                                    </td>
                                                    <td className="py-4 px-3 text-right font-black text-rose-600">
                                                        {formatINR(c.outstanding_balance)}
                                                    </td>
                                                    <td className="py-4 px-4 text-center">
                                                        <span className="px-2.5 py-1 bg-slate-100 rounded-full font-black text-[10px] text-slate-700">
                                                            {c.collection_rate}%
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4 text-center">
                                                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${
                                                            c.status === 'Settled'
                                                                ? 'bg-emerald-100 text-emerald-800'
                                                                : c.status === 'Partially Paid'
                                                                ? 'bg-amber-100 text-amber-800'
                                                                : 'bg-rose-100 text-rose-800'
                                                        }`}>
                                                            {c.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB: VENDOR PROCUREMENT CYCLE */}
                    {activeTab === 'vendor' && (
                        <div className="bg-white border border-slate-100 rounded-[36px] p-6 lg:p-8 shadow-sm">
                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 tracking-tight">
                                        Vendor Procurement Cycle: PO Sent vs Invoices Received vs Payments
                                    </h3>
                                    <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-1">
                                        Purchase orders committed, vendor invoices billed, payments disbursed, and pending payables
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-black text-indigo-700 bg-indigo-50 px-3 py-1 rounded-full">
                                        PO Committed: {formatINR(varianceData.kpis.total_po_committed)}
                                    </span>
                                    <span className="text-xs font-black text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full">
                                        Paid: {formatINR(varianceData.kpis.total_vendor_paid)}
                                    </span>
                                    <span className="text-xs font-black text-amber-700 bg-amber-50 px-3 py-1 rounded-full">
                                        Payables Due: {formatINR(varianceData.kpis.outstanding_payables)}
                                    </span>
                                </div>
                            </div>

                            {filteredVendor.length === 0 ? (
                                <div className="text-center py-16">
                                    <Truck size={36} className="text-slate-300 mx-auto mb-2" />
                                    <p className="text-sm font-black text-slate-700">No vendor procurement records found</p>
                                    <p className="text-xs text-slate-400 mt-1">No purchase orders or vendor bills recorded under current filters.</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse text-xs">
                                        <thead>
                                            <tr className="border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-400 bg-slate-50/50">
                                                <th className="py-4 px-4">Vendor Partner</th>
                                                <th className="py-4 px-4">Site / Project</th>
                                                <th className="py-4 px-4">PO References</th>
                                                <th className="py-4 px-3 text-right">PO Committed</th>
                                                <th className="py-4 px-3 text-right">Invoices Received</th>
                                                <th className="py-4 px-3 text-right">Payments Made</th>
                                                <th className="py-4 px-3 text-right">Pending Invoicing</th>
                                                <th className="py-4 px-3 text-right">Outstanding Payables</th>
                                                <th className="py-4 px-4 text-center">Settlement %</th>
                                                <th className="py-4 px-4 text-center">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50 font-bold text-slate-700">
                                            {filteredVendor.map((v) => (
                                                <tr key={v.id} className="hover:bg-slate-50/80 transition-colors">
                                                    <td className="py-4 px-4 font-black text-slate-900">
                                                        {v.vendor_name}
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-500">
                                                        {v.project_name}
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-600 font-mono text-[11px] max-w-xs truncate">
                                                        {v.po_summary}
                                                    </td>
                                                    <td className="py-4 px-3 text-right font-bold text-slate-900">
                                                        {formatINR(v.po_committed)}
                                                    </td>
                                                    <td className="py-4 px-3 text-right text-slate-700">
                                                        {formatINR(v.invoiced_amount)}
                                                    </td>
                                                    <td className="py-4 px-3 text-right font-black text-indigo-600">
                                                        {formatINR(v.paid_amount)}
                                                    </td>
                                                    <td className="py-4 px-3 text-right text-slate-400">
                                                        {v.pending_invoicing > 0 ? formatINR(v.pending_invoicing) : '-'}
                                                    </td>
                                                    <td className="py-4 px-3 text-right font-black text-amber-600">
                                                        {formatINR(v.outstanding_payables)}
                                                    </td>
                                                    <td className="py-4 px-4 text-center">
                                                        <span className="px-2.5 py-1 bg-slate-100 rounded-full font-black text-[10px] text-slate-700">
                                                            {v.settlement_rate}%
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4 text-center">
                                                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${
                                                            v.status === 'Fully Paid'
                                                                ? 'bg-emerald-100 text-emerald-800'
                                                                : v.status === 'Partially Paid'
                                                                ? 'bg-amber-100 text-amber-800'
                                                                : 'bg-indigo-100 text-indigo-800'
                                                        }`}>
                                                            {v.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB: LABOUR WORKFORCE CYCLE */}
                    {activeTab === 'labour' && (
                        <div className="bg-white border border-slate-100 rounded-[36px] p-6 lg:p-8 shadow-sm">
                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 tracking-tight">
                                        Labour Workforce Cycle: Attendance Salary vs Actual Payments
                                    </h3>
                                    <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-1">
                                        Daily shift wages accrued from attendance vs cash advances & salary payouts made to workers
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-black text-purple-700 bg-purple-50 px-3 py-1 rounded-full">
                                        Earned: {formatINR(varianceData.kpis.total_labour_attendance)}
                                    </span>
                                    <span className="text-xs font-black text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full">
                                        Paid: {formatINR(varianceData.kpis.total_labour_paid)}
                                    </span>
                                    <span className="text-xs font-black text-amber-700 bg-amber-50 px-3 py-1 rounded-full">
                                        Liability Due: {formatINR(varianceData.kpis.wage_liability_payable)}
                                    </span>
                                </div>
                            </div>

                            {filteredLabour.length === 0 ? (
                                <div className="text-center py-16">
                                    <HardHat size={36} className="text-slate-300 mx-auto mb-2" />
                                    <p className="text-sm font-black text-slate-700">No workforce labour records found</p>
                                    <p className="text-xs text-slate-400 mt-1">No attendance logs or worker payouts recorded under current filters.</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse text-xs">
                                        <thead>
                                            <tr className="border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-400 bg-slate-50/50">
                                                <th className="py-4 px-4">Employee / Worker</th>
                                                <th className="py-4 px-4">Site / Project</th>
                                                <th className="py-4 px-4">Workload Done</th>
                                                <th className="py-4 px-4 text-center">Pay Structure</th>
                                                <th className="py-4 px-3 text-right">Attendance Earned Salary</th>
                                                <th className="py-4 px-3 text-right">Actual Payments Made</th>
                                                <th className="py-4 px-3 text-right">Remaining Liability</th>
                                                <th className="py-4 px-4 text-center">Settlement %</th>
                                                <th className="py-4 px-4 text-center">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50 font-bold text-slate-700">
                                            {filteredLabour.map((l) => (
                                                <tr key={l.id} className="hover:bg-slate-50/80 transition-colors">
                                                    <td className="py-4 px-4 font-black text-slate-900">
                                                        <div className="flex items-center gap-2">
                                                            <span className="p-1.5 bg-purple-50 text-purple-600 rounded-lg">
                                                                <HardHat size={14} />
                                                            </span>
                                                            <span>{l.employee_name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-500">
                                                        {l.project_name}
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-700">
                                                        {l.workload}
                                                    </td>
                                                    <td className="py-4 px-4 text-center">
                                                        <span className="px-2 py-0.5 bg-slate-100 rounded-md text-[11px] font-bold text-slate-600">
                                                            {l.pay_type}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-3 text-right font-black text-purple-700">
                                                        {formatINR(l.attendance_earned)}
                                                    </td>
                                                    <td className="py-4 px-3 text-right font-black text-emerald-600">
                                                        {formatINR(l.actual_paid)}
                                                    </td>
                                                    <td className="py-4 px-3 text-right font-black text-amber-600">
                                                        {formatINR(l.wage_liability)}
                                                    </td>
                                                    <td className="py-4 px-4 text-center">
                                                        <span className="px-2.5 py-1 bg-slate-100 rounded-full font-black text-[10px] text-slate-700">
                                                            {l.settlement_rate}%
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4 text-center">
                                                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${
                                                            l.status === 'Settled'
                                                                ? 'bg-emerald-100 text-emerald-800'
                                                                : l.status === 'Partially Paid'
                                                                ? 'bg-amber-100 text-amber-800'
                                                                : 'bg-rose-100 text-rose-800'
                                                        }`}>
                                                            {l.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
